import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import type { CustomerAccount, Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";

export interface UserAccountQuery {
    page: number;
    size: number;
    name?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface UserAccountPageProps {
    loadAccounts?: (query: UserAccountQuery) => Promise<Page<CustomerAccount>>;
}

const defaultQuery: UserAccountQuery = { page: 1, size: 10 };

/** 复用 Vue 用户账户分页接口。 */
export async function loadUserAccounts(query: UserAccountQuery): Promise<Page<CustomerAccount>> {
    const response = await request.service.get<Page<CustomerAccount>>("/admin/customer/account/paging", {
        params: query
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatAmount = (value?: number | null) =>
    value === undefined || value === null
        ? DEFAULT_VALUE
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 费用管理-用户账户列表，覆盖 Vue 账户字段、筛选、排序和详情入口。 */
export function UserAccountPage({ loadAccounts = loadUserAccounts }: UserAccountPageProps) {
    const [rows, setRows] = useState<CustomerAccount[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<UserAccountQuery>(defaultQuery);
    const [name, setName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadAccounts(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("用户账户加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadAccounts, query]);

    // 将 Vue ProTable 搜索草稿转换为分页接口参数，并回到第一页。
    const submitFilters = () => {
        const nextQuery: UserAccountQuery = { ...defaultQuery };
        if (name.trim()) nextQuery.name = name.trim();
        setErrorMessage("");
        setQuery(nextQuery);
    };

    // 清空账户名称筛选并恢复 Vue 列表首屏参数。
    const resetFilters = () => {
        setName("");
        setErrorMessage("");
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 用户账户列表的账户字段、金额格式和更新时间排序。
    const columns: TableColumnProps<CustomerAccount>[] = [
        {
            key: "code",
            label: "用户ID",
            minWidth: 138,
            prop: "code",
            render: ({ row }) => <Link to={`/expenseManagement/userAccount/detail/${row.id}`}>{row.code}</Link>
        },
        {
            key: "name",
            label: "用户账户",
            minWidth: 228,
            prop: "name",
            render: ({ row }) => row.name || DEFAULT_VALUE
        },
        {
            key: "balance",
            label: "账户余额 (元)",
            minWidth: 214,
            prop: "balance",
            render: ({ row }) => formatAmount(row.balance)
        },
        {
            key: "updatedTime",
            label: "更新时间",
            minWidth: 130,
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => formatDate(row.updatedTime)
        }
    ];

    return (
        <section aria-label="user-account-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<CustomerAccount>
                    data={rows}
                    columns={columns}
                    pagination={false}
                    rowKey="id"
                    onSortChange={({ prop, order }) => {
                        if (prop !== "updatedTime") return;
                        setQuery(current => {
                            const nextQuery: UserAccountQuery = { ...current, page: 1 };
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                            if (order) {
                                nextQuery.sortKey = "updated_time";
                                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                            }
                            return nextQuery;
                        });
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                用户账户
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item>
                                    <Input
                                        aria-label="账户名称"
                                        maxLength={32}
                                        placeholder="请输入账户名称搜索"
                                        value={name}
                                        onChange={event => setName(event.target.value)}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button
                                        aria-label="查询"
                                        htmlType="submit"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                    >
                                        搜索
                                    </Button>
                                    <Button
                                        aria-label="重置"
                                        className="table-filter-reset-button"
                                        icon={<TableSearchIcon name="reset" />}
                                        title="重置"
                                        onClick={resetFilters}
                                    />
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
